import {
  FIBONACCI_SEQUENCE,
  type FibonacciFieldConfigurationDto,
  type FieldConfigurationDto,
} from '@tempo-io/fim3-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { Select } from '@tempo-io/sui-select';
import { Slider } from '@tempo-io/sui-slider';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FlexDiv } from './Styled';

type Option = { value: string; label: string };

const NONE_OPTION = 'None';

const getFibonacciRangeClass = (value: number, maxValue: number) => {
  const ratio = value / maxValue;
  if (ratio >= 0.8) return '[&_[data-slot=slider-range]]:bg-bg-success';
  if (ratio >= 0.5) return '[&_[data-slot=slider-range]]:bg-bg-warning';
  if (ratio >= 0.2) return '[&_[data-slot=slider-range]]:bg-bg-warning-subtle';
  return '[&_[data-slot=slider-range]]:bg-bg-brand';
};

export const FibonacciFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  useDropdown,
  required,
  autoFocus,
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  useDropdown?: boolean;
  required?: boolean;
  autoFocus?: boolean;
}) => {
  const { t: a11y } = useTranslation('translation');
  const initializedRef = useRef(false);

  const isFibonacci = fieldConfiguration.fieldType === 'FIBONACCI';
  const maxValue = isFibonacci
    ? (fieldConfiguration as FibonacciFieldConfigurationDto).maxValue
    : 0;
  const validFibNumbers = FIBONACCI_SEQUENCE.filter((f) => f <= maxValue);

  useEffect(() => {
    if (!isFibonacci) return;
    if (!initializedRef.current && (!values[0] || values[0].trim() === '')) {
      const defaultValue = validFibNumbers[0];
      if (defaultValue !== undefined && !useDropdown) {
        initializedRef.current = true;
        setValues([defaultValue.toString()]);
      }
    }
  }, [isFibonacci, values, validFibNumbers, setValues, useDropdown]);

  if (!isFibonacci) return null;

  const dropdownOptions: Option[] = (
    useDropdown && !required
      ? [NONE_OPTION, ...validFibNumbers.map(String)]
      : validFibNumbers.map(String)
  ).map((option) => ({ value: option, label: option }));

  const marks = validFibNumbers.map((fib, index) => ({
    value: index,
    label: fib.toString(),
  }));

  const hasValue = values[0] && values[0].trim() !== '';
  const currentValue = hasValue && values[0] ? parseInt(values[0], 10) : null;
  const currentIndex =
    currentValue !== null
      ? validFibNumbers.indexOf(currentValue as (typeof validFibNumbers)[number])
      : -1;
  const displayIndex = currentIndex >= 0 ? currentIndex : 0;

  const handleSliderChange = (newIndex: number[]) => {
    const [index] = newIndex;
    if (index === undefined) return;
    const fibValue = validFibNumbers[index];
    if (fibValue !== undefined) {
      setValues([fibValue.toString()]);
    }
  };

  const handleSliderInteraction = () => {
    if (!hasValue) {
      const defaultValue = validFibNumbers[displayIndex];
      if (defaultValue !== undefined) {
        setValues([defaultValue.toString()]);
      }
    }
  };

  const slider = (
    <Slider
      className={cn(
        'fs-exclude',
        getFibonacciRangeClass(currentValue ?? validFibNumbers[0] ?? 1, maxValue),
      )}
      data-dd-action-name="FIM 3 Custom Field - fibonacci slider redacted"
      data-testid="fim3-custom-field-input-fibonacci-slider"
      aria-label={a11y(($) => $.intake.a11y.fibonacciField)}
      value={[displayIndex]}
      onValueChange={handleSliderChange}
      onPointerDown={handleSliderInteraction}
      min={0}
      max={validFibNumbers.length - 1}
      step={1}
      marks={marks}
    />
  );

  if (useDropdown) {
    const displayValue = values[0] || null;
    const selectedOption = displayValue ? { value: displayValue, label: displayValue } : null;

    return (
      <FlexDiv className="pb-6">
        <div
          className="mr-3 w-[100px] shrink-0"
          data-testid="fim3-custom-field-input-fibonacci-autocomplete"
        >
          <Select<Option>
            className="fs-exclude"
            data-dd-action-name="FIM 3 Custom Field - fibonacci autocomplete redacted"
            aria-label={a11y(($) => $.intake.a11y.fibonacciField)}
            options={dropdownOptions}
            value={selectedOption}
            onChange={(option) => {
              if (option === null || option.value === NONE_OPTION) {
                setValues(['']);
              } else {
                setValues([option.value]);
              }
            }}
            autoFocus={autoFocus}
          />
        </div>
        {slider}
      </FlexDiv>
    );
  }

  return <div className="w-full pt-1 pr-3 pb-6 pl-1">{slider}</div>;
};
